// StoreContext — mağazanın sepet / favori / fiyat alarmı / okunmamış mesaj durumu.
//
// Favoriler, fiyat alarmları ve okunmamış mesaj sayısı API'dendir (storeApi; store-ext uçları).
// Mağaza ekranlarından biri ilk açıldığında (useStoreData) yüklenir — panelin geri kalanı
// mağaza isteği yapmaz.
// Sepet ORTAKTIR (16.10.2026): hesaba bağlı, API'de (store-ext get-store-cart / set-store-cart-item);
// website de aynı sepeti kullanır. Yerelde (localStorage, anahtar store:<userID>) yalnız ürünlerin
// anlık görüntüsü önbellek olarak durur. Açılışta, sekmeye dönüşte ve pencere odaklanınca sepet
// API'den eşitlenir; ekle/çıkar önce yerelde (anında), sonra API'de yapılır — API reddederse yeniden
// eşitlenir. Ortak sepetten önce yerelde kalmış ürünler bir kez hesabın sepetine aktarılır.
// YALNIZ satışa açık, yayındaki ürünler girer (üye ilanları satılmaz; alıcı satıcıyla iletişime geçer).
// Sepet sayfası açılınca ürünler API'den tazelenir (website storeCartItems ile aynı kural).
//
// Sağlayıcı App düzeyindedir (panel header'ındaki sepet rozeti de okur). Giriş/çıkışta
// userID değişince sepet o kullanıcının kaydından, favori/alarmlar API'den yeniden yüklenir.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  alarmKur,
  alarmSil,
  favoriAyarla,
  favoriler,
  fiyatAlarmlari,
  ilanGetir,
  okunmamisSayisi,
  sepetBirlestir,
  sepetGetir,
  sepetYaz,
  storeToken,
} from "./storeApi";

const StoreContext = createContext(null);
// FE-89 (21.10.2026): header'daki sepet rozeti yalnız adedi okur. Tam bağlamı dinlediğinde mağazada favori/alarm/
// okunmamış her değiştiğinde header da yeniden çiziliyordu. Adet ayrı bağlamda; yalnız adet değişince güncellenir.
const StoreSepetSayisiContext = createContext(0);

function kullaniciID() {
  try {
    return localStorage.getItem("userID") || "";
  } catch (e) {
    return "";
  }
}

const storageKey = (id) => `store:${id || "anon"}`;

/** Sepete girebilir mi: Troy Depo ürünü + yayında. */
// Sepete yalnız satış izni açık (open_to_sale) ve yayındaki ilanlar girer.
export const sepeteUygun = (u) => !!u && !!u.satista && Number(u.durumKod) === 2 && !!u.slug;

function loadCart(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    const cart = parsed && typeof parsed.cart === "object" && parsed.cart ? parsed.cart : {};
    // Eski (dummy) biçim {slug: adet} ve uygun olmayan ürünler atılır.
    const temiz = {};
    Object.entries(cart).forEach(([slug, v]) => {
      if (v && typeof v === "object" && sepeteUygun(v.urun)) temiz[slug] = { adet: Math.max(1, Number(v.adet) || 1), urun: v.urun };
    });
    return temiz;
  } catch (e) {
    return {};
  }
}

const FAV_BOS = { yukleniyor: true, hata: "", idler: [], urunler: [] };
const ALARM_BOS = { yukleniyor: true, hata: "", liste: [] };

export function StoreProvider({ children }) {
  // Her render'da güncel kullanıcı okunur (App, rota değişiminde yeniden çizer).
  const uid = kullaniciID();
  const key = storageKey(uid);
  const [sepet, setSepet] = useState(() => ({ key, cart: loadCart(key) }));
  const cart = sepet.key === key ? sepet.cart : loadCart(key);

  const [aktif, setAktif] = useState(false); // bir mağaza ekranı açıldı mı
  const [fav, setFav] = useState(FAV_BOS);
  const [alarm, setAlarm] = useState(ALARM_BOS);
  const [okunmamis, setOkunmamis] = useState(0);

  // Kullanıcı değişti → o kullanıcının sepetine geç.
  useEffect(() => {
    if (sepet.key !== key) setSepet({ key, cart: loadCart(key) });
  }, [key, sepet.key]);

  useEffect(() => {
    try {
      localStorage.setItem(sepet.key, JSON.stringify({ cart: sepet.cart }));
    } catch (e) {
      /* yoksay */
    }
  }, [sepet]);

  const setCart = useCallback((f) => setSepet((s) => ({ ...s, cart: f(s.cart) })), []);
  const cartRef = useRef(cart);
  cartRef.current = cart;

  /* ── Ortak sepet eşitleme (API → yerel önbellek) ── */
  const esitleNo = useRef(0);
  const sepetEsitle = useCallback(async () => {
    if (!storeToken()) return;
    const no = ++esitleNo.current;
    let r = await sepetGetir();
    if (!r.ok || no !== esitleNo.current) return;
    // Ortak sepetten önce yerelde kalmış ürünler bir kez hesaba aktarılır.
    const bayrak = `${key}:ortak`;
    let aktarildi = false;
    try {
      aktarildi = localStorage.getItem(bayrak) === "1";
    } catch (e) {
      aktarildi = true;
    }
    if (!aktarildi) {
      const sunucuda = new Set(r.items.map((x) => x.slug));
      const yerel = Object.entries(cartRef.current)
        .filter(([slug]) => !sunucuda.has(slug))
        .map(([slug, k]) => ({ slug, qty: k.adet }));
      if (yerel.length) {
        const m = await sepetBirlestir(yerel);
        if (m.ok) r = m;
      }
      try {
        localStorage.setItem(bayrak, "1");
      } catch (e) {
        /* yoksay */
      }
      if (no !== esitleNo.current) return;
    }
    const mevcut = cartRef.current;
    const eksik = r.items.filter((x) => !mevcut[x.slug]).map((x) => x.slug);
    const urunler = {};
    await Promise.all(
      eksik.map((slug) =>
        ilanGetir(slug).then((g) => {
          if (g.ok) urunler[slug] = g.urun;
        }),
      ),
    );
    if (no !== esitleNo.current) return;
    setCart((c) => {
      const yeni = {};
      r.items.forEach((x) => {
        const u = c[x.slug]?.urun || urunler[x.slug];
        if (u && sepeteUygun(u)) yeni[x.slug] = { adet: Math.max(1, Number(x.qty) || 1), urun: u };
      });
      return yeni;
    });
  }, [key, setCart]);

  // Açılışta / kullanıcı değişince ve sekmeye dönüldüğünde (sitede değişmiş olabilir) eşitle.
  useEffect(() => {
    sepetEsitle();
    const odak = () => {
      if (!document.hidden) sepetEsitle();
    };
    window.addEventListener("focus", odak);
    document.addEventListener("visibilitychange", odak);
    return () => {
      window.removeEventListener("focus", odak);
      document.removeEventListener("visibilitychange", odak);
    };
  }, [sepetEsitle]);

  // Yerel değişikliği API'ye yaz; reddedilirse (satıldı vb.) sepeti API'den yeniden eşitle.
  const sunucuyaYaz = useCallback(
    (slug, adet) => {
      if (!storeToken()) return;
      sepetYaz(slug, adet).then((r) => {
        if (!r.ok) sepetEsitle();
      });
    },
    [sepetEsitle],
  );

  /* ── API verileri ── */
  const favTazele = useCallback(async () => {
    if (!storeToken()) {
      setFav({ ...FAV_BOS, yukleniyor: false });
      return;
    }
    const r = await favoriler();
    if (r.ok) setFav({ yukleniyor: false, hata: "", idler: r.idler, urunler: r.urunler });
    else setFav((s) => ({ ...s, yukleniyor: false, hata: r.mesaj }));
  }, []);

  const alarmTazele = useCallback(async () => {
    if (!storeToken()) {
      setAlarm({ ...ALARM_BOS, yukleniyor: false });
      return;
    }
    const r = await fiyatAlarmlari();
    if (r.ok) setAlarm({ yukleniyor: false, hata: "", liste: r.alarmlar });
    else setAlarm((s) => ({ ...s, yukleniyor: false, hata: r.mesaj }));
  }, []);

  const okunmamisTazele = useCallback(async () => {
    setOkunmamis(storeToken() ? await okunmamisSayisi() : 0);
  }, []);

  useEffect(() => {
    if (!aktif) return;
    setFav(FAV_BOS);
    setAlarm(ALARM_BOS);
    favTazele();
    alarmTazele();
    okunmamisTazele();
  }, [aktif, uid, favTazele, alarmTazele, okunmamisTazele]);

  // Okunmamış mesaj rozeti: socket yokken mağaza açıkken 30 sn'de bir yoklanır.
  useEffect(() => {
    if (!aktif) return undefined;
    const t = setInterval(() => {
      if (!document.hidden) okunmamisTazele();
    }, 30000);
    return () => clearInterval(t);
  }, [aktif, okunmamisTazele]);

  const activate = useCallback(() => setAktif(true), []);

  /* ── Sepet (yalnız Troy Depo ürünleri) ── */
  const addToCart = useCallback(
    (u, adet = 1) => {
      if (!sepeteUygun(u)) return false;
      const mevcut = cartRef.current[u.slug]?.adet || 0;
      const yeni = Math.min(Math.max(1, u.stok || 1), mevcut + Math.max(1, adet));
      setCart((c) => ({ ...c, [u.slug]: { adet: yeni, urun: u } }));
      sunucuyaYaz(u.slug, yeni);
      return true;
    },
    [setCart, sunucuyaYaz],
  );

  const setCartQty = useCallback(
    (slug, adet) => {
      const k = cartRef.current[slug];
      if (!k) return;
      const deger = adet <= 0 ? 0 : Math.min(Math.max(1, k.urun.stok || 1), Math.floor(adet));
      setCart((c) => {
        if (!c[slug]) return c;
        const yeni = { ...c };
        if (deger <= 0) delete yeni[slug];
        else yeni[slug] = { ...c[slug], adet: deger };
        return yeni;
      });
      sunucuyaYaz(slug, deger);
    },
    [setCart, sunucuyaYaz],
  );

  const removeFromCart = useCallback(
    (slug) => {
      setCart((c) => {
        const yeni = { ...c };
        delete yeni[slug];
        return yeni;
      });
      sunucuyaYaz(slug, 0);
    },
    [setCart, sunucuyaYaz],
  );

  /** Sepetteki ürünleri API'den gelen güncel hâlleriyle değiştirir ({slug: ürün|null});
   *  artık uygun olmayanlar (satıldı, kaldırıldı) çıkar, tazelenemeyenler olduğu gibi kalır. */
  const refreshCartItems = useCallback(
    (guncel) =>
      setCart((c) => {
        const yeni = {};
        Object.entries(c).forEach(([slug, k]) => {
          if (!(slug in guncel)) {
            yeni[slug] = k;
            return;
          }
          const u = guncel[slug];
          if (sepeteUygun(u)) yeni[slug] = { adet: k.adet, urun: u };
        });
        return yeni;
      }),
    [setCart],
  );

  const clearCart = useCallback(() => setCart(() => ({})), [setCart]);

  /* ── Favori ── */
  const toggleFavorite = useCallback(
    async (u) => {
      const id = String(u?.listingID || "");
      if (!id) return { ok: false, mesaj: "İlan bulunamadı." };
      const ekle = !fav.idler.includes(id);
      const r = await favoriAyarla(id, ekle);
      if (!r.ok) return r;
      setFav((s) => ({
        ...s,
        idler: r.favori ? [...s.idler.filter((x) => x !== id), id] : s.idler.filter((x) => x !== id),
        urunler: r.favori
          ? s.urunler.some((x) => x.listingID === id)
            ? s.urunler
            : [u, ...s.urunler]
          : s.urunler.filter((x) => x.listingID !== id),
      }));
      return r;
    },
    [fav.idler],
  );

  /* ── Fiyat alarmı (hedef null = her düşüşte) ── */
  const setPriceAlarm = useCallback(
    async (listingID, hedef) => {
      const r = await alarmKur(listingID, hedef);
      if (r.ok) alarmTazele();
      return r;
    },
    [alarmTazele],
  );

  const removePriceAlarm = useCallback(async (listingID) => {
    const r = await alarmSil(listingID);
    if (r.ok) setAlarm((s) => ({ ...s, liste: s.liste.filter((a) => a.listingID !== listingID) }));
    return r;
  }, []);

  const value = useMemo(() => {
    const cartItems = Object.values(cart).map((k) => ({ ...k.urun, sepetAdet: Math.max(1, Number(k.adet) || 1) }));
    return {
      activate,
      cart: Object.fromEntries(Object.entries(cart).map(([slug, k]) => [slug, k.adet])),
      cartItems,
      cartCount: cartItems.reduce((t, u) => t + u.sepetAdet, 0),
      addToCart,
      setCartQty,
      removeFromCart,
      refreshCartItems,
      clearCart,
      favorites: fav.idler,
      favItems: fav.urunler,
      favCount: fav.idler.length,
      favLoading: fav.yukleniyor,
      favError: fav.hata,
      isFavorite: (listingID) => fav.idler.includes(String(listingID || "")),
      toggleFavorite,
      refreshFavorites: favTazele,
      alarms: alarm.liste,
      alarmLoading: alarm.yukleniyor,
      alarmError: alarm.hata,
      alarmFor: (listingID) => alarm.liste.find((a) => a.listingID === String(listingID || "")) || null,
      setAlarm: setPriceAlarm,
      removeAlarm: removePriceAlarm,
      refreshAlarms: alarmTazele,
      unreadCount: okunmamis,
      refreshUnread: okunmamisTazele,
    };
  }, [
    activate,
    cart,
    fav,
    alarm,
    okunmamis,
    addToCart,
    setCartQty,
    removeFromCart,
    refreshCartItems,
    clearCart,
    toggleFavorite,
    favTazele,
    setPriceAlarm,
    removePriceAlarm,
    alarmTazele,
    okunmamisTazele,
  ]);

  return (
    <StoreContext.Provider value={value}>
      <StoreSepetSayisiContext.Provider value={value.cartCount}>{children}</StoreSepetSayisiContext.Provider>
    </StoreContext.Provider>
  );
}

/** FE-89: yalnız sepet adedi (header rozeti) — favori/alarm değişimlerinde yeniden çizilmez. */
export function useStoreSepetSayisi() {
  return useContext(StoreSepetSayisiContext);
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore, StoreProvider içinde kullanılmalı");
  return ctx;
}

/** Mağaza ekranları: favori/alarm/okunmamış verisini (bir kez) yükletir ve bağlamı döndürür. */
export function useStoreData() {
  const ctx = useStore();
  const { activate } = ctx;
  useEffect(() => {
    activate();
  }, [activate]);
  return ctx;
}
